import {
  currentQueuedModalAtom,
  dequeueModalAtom,
  recordPacedLaunchModalDismissedAtom,
  seenModalIdsAtom,
} from '@/atoms/modalQueueAtoms';
import { type ModalId, ModalViewAction } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useMutateMarkModalAsViewed } from './mutation/useMutateMarkModalAsViewed';

export type QueuedModalCloseOptions = {
  action?: ModalViewAction;
  increment_count?: boolean;
  hide_forever?: boolean;
  /** Skip the pacing slot and the R5 journey record. Help-menu / Force-show closes must not spend the cap. */
  skipPacing?: boolean;
};

export type QueuedModalCloseWithOptions = (options?: QueuedModalCloseOptions) => void;

/**
 * Hook for modal components to subscribe to the queue
 * Returns whether this modal should be open based on the current modal in the queue
 *
 * @param modalId - The unique ID of this modal
 * @returns Object with isOpen state and close handlers
 */
export function useQueuedModal(modalId: ModalId) {
  const currentModal = useAtomValue(currentQueuedModalAtom);
  const setSeenModalIds = useSetAtom(seenModalIdsAtom);
  const dequeueModal = useSetAtom(dequeueModalAtom);
  const recordPacedDismissal = useSetAtom(recordPacedLaunchModalDismissedAtom);
  const { mutate: markModalAsViewedBackend } = useMutateMarkModalAsViewed();

  const isOpen = currentModal?.id === modalId;
  const dismissible = currentModal?.dismissible ?? true;

  /** Queue close with `action` / `hide_forever` / `increment_count`. Not an event handler. */
  const closeWithOptions: QueuedModalCloseWithOptions = useCallback(
    (options) => {
      // Before seen-state write — else launch list drops id and pacing guard fails.
      if (!options?.skipPacing) {
        recordPacedDismissal(modalId);
      }
      setSeenModalIds((prev) => new Set([...(prev ?? []), modalId]));
      markModalAsViewedBackend({
        modal_id: modalId,
        increment_count: options?.increment_count ?? true,
        ...(options?.action != null ? { action: options.action } : {}),
        ...(options?.hide_forever != null ? { hide_forever: options.hide_forever } : {}),
      });
      dequeueModal(modalId);
    },
    [
      modalId,
      recordPacedDismissal,
      setSeenModalIds,
      dequeueModal,
      markModalAsViewedBackend,
    ]
  );

  /** Event handler: `onClick={handleClose}`. Same pipeline as `closeWithOptions()` with defaults. */
  const handleClose = useCallback(() => {
    closeWithOptions();
  }, [closeWithOptions]);

  /** Radix `onOpenChange`. `true` is a no-op; `false` is a user dismiss (Escape). */
  const onOpenChange = useCallback(
    (open: boolean) => {
      if (open) return;
      closeWithOptions({ action: ModalViewAction.dismiss });
    },
    [closeWithOptions]
  );

  return {
    isOpen,
    dismissible,
    handleClose,
    onOpenChange,
    closeWithOptions,
  };
}
